import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { Check, FileText, Target, Shield, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { 
  DynamicBreadcrumbs, 
  CoachBridgeCopy, 
  InstructionalFade,
  ValidationPulse,
} from './InstructionalComponents';

// Animation variants
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};

// Breadcrumb items
const BREADCRUMB_ITEMS = [
  { id: 'orientation', label: 'Orientation' },
  { id: 'theory', label: 'Conversation Tools' },
  { id: 'prep', label: 'Conversation Plan' },
  { id: 'simulation', label: 'Simulation' },
];

const OPENING_STANCES = [
  {
    id: 'inquiry_first',
    label: 'Inquiry First',
    description: 'Lead with questions to understand before addressing.',
    recommended: true,
  },
  {
    id: 'complaint_first',
    label: 'Lead with the Concern',
    description: 'Name the concern directly from the start.',
    recommended: false,
  },
  {
    id: 'reassurance_first',
    label: 'Lower the Threat First',
    description: 'Soften the conversation with support before addressing.',
    recommended: false,
  },
  {
    id: 'standards_first',
    label: 'Lead with Expectations',
    description: 'Frame around professional expectations and optics.',
    recommended: false,
  },
];

const NEEDS_TO_UNDERSTAND = [
  {
    id: 'teacher_account',
    label: 'Teacher\'s Account',
    description: 'What happened from Ms. Ortega\'s perspective',
  },
  {
    id: 'student_impact',
    label: 'Student Experience',
    description: 'How the student experienced the moment',
  },
  {
    id: 'family_history',
    label: 'Relevant Context',
    description: 'Family, school, or situational context that may be relevant',
  },
];

const MUST_NOT_AVOID = [
  {
    id: 'student_impact',
    label: 'Name Student Impact',
    description: 'Ensure the student\'s experience is acknowledged',
  },
  {
    id: 'accountability',
    label: 'Establish Clear Accountability',
    description: 'Establish ownership without blame',
  },
  {
    id: 'next_steps',
    label: 'Leave with a Concrete Next Step',
    description: 'Leave with a defined path forward',
  },
];

export function PrepCardBuilder() {
  const { state, updatePrepCard, completePrepCard, setView, startSimulation } = useSimulation();
  const { prepCard } = state;
  
  const [localPrepCard, setLocalPrepCard] = useState({
    openingStance: prepCard.openingStance,
    needsToUnderstand: [...prepCard.needsToUnderstand],
    mustNotAvoid: [...prepCard.mustNotAvoid],
    equityLens: prepCard.equityLens,
  });
  
  const [hasInteracted, setHasInteracted] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const handleStanceSelect = (stanceId) => {
    setLocalPrepCard(prev => ({ ...prev, openingStance: stanceId }));
    setHasInteracted(true);
  };

  const toggleNeedToUnderstand = (itemId) => {
    setLocalPrepCard(prev => ({
      ...prev,
      needsToUnderstand: prev.needsToUnderstand.includes(itemId)
        ? prev.needsToUnderstand.filter(id => id !== itemId)
        : [...prev.needsToUnderstand, itemId],
    }));
    setHasInteracted(true);
  };

  const toggleMustNotAvoid = (itemId) => {
    setLocalPrepCard(prev => ({
      ...prev,
      mustNotAvoid: prev.mustNotAvoid.includes(itemId)
        ? prev.mustNotAvoid.filter(id => id !== itemId)
        : [...prev.mustNotAvoid, itemId],
    }));
    setHasInteracted(true);
  };

  const toggleEquityLens = () => {
    setLocalPrepCard(prev => ({ ...prev, equityLens: !prev.equityLens }));
    setHasInteracted(true);
  };

  const handleSave = () => {
    updatePrepCard(localPrepCard);
  };

  const handleStartSimulation = () => {
    if (!isComplete) {
      setAttemptedSubmit(true);
      return;
    }
    updatePrepCard(localPrepCard);
    completePrepCard();
    startSimulation();
  };

  const isComplete = localPrepCard.openingStance && 
    localPrepCard.needsToUnderstand.length > 0 &&
    localPrepCard.mustNotAvoid.length > 0;
  
  // Validation states for each section
  const showStanceValidation = attemptedSubmit && !localPrepCard.openingStance;
  const showUnderstandValidation = attemptedSubmit && localPrepCard.needsToUnderstand.length === 0;
  const showAvoidValidation = attemptedSubmit && localPrepCard.mustNotAvoid.length === 0;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={stagger}
      className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-12"
      data-testid="prep-card-builder"
    >
      {/* Breadcrumbs */}
      <DynamicBreadcrumbs items={BREADCRUMB_ITEMS} currentIndex={2} />

      {/* Coach Bridge Copy */}
      <CoachBridgeCopy 
        message="You've seen the moves that build trust and hold accountability. Now, put them together into a plan you'll carry into the conversation."
      />

      {/* Header */}
      <motion.div variants={fadeUp} className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-6 h-6 text-[#0055A5]" strokeWidth={1.5} />
          <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
            Plan
          </span>
          <span className="mx-2 text-xs text-slate-300">|</span>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
            Module 08
          </span>
        </div>
        <h1 className="text-3xl lg:text-4xl font-headline font-bold text-[#002B45] tracking-tight mb-4">
          Conversation Plan
        </h1>
        <p className="text-lg text-[#475569] max-w-2xl">
          This isn't a quiz. It's the plan you'll carry into the conversation. These selections will be tracked to identify alignment or drift during the rehearsal.
        </p>
      </motion.div>

      {/* Opening Stance Section */}
      <motion.section variants={fadeUp} className="mb-12" data-testid="prep-opening-stance">
        <div className="flex items-center gap-3 mb-6">
          <Target className="w-5 h-5 text-[#002B45]" strokeWidth={1.5} />
          <h2 className="text-xl font-headline font-semibold text-[#002B45]">
            Opening Stance
          </h2>
          {showStanceValidation && (
            <span className="text-xs font-semibold text-[#DA291C] animate-pulse">
              Required
            </span>
          )}
        </div>
        
        {/* Instructional text that fades after interaction */}
        <InstructionalFade hasInteracted={hasInteracted && localPrepCard.openingStance}>
          How will you frame the conversation from the first moment? Your choice sets the tone.
        </InstructionalFade>
        
        <ValidationPulse show={showStanceValidation}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {OPENING_STANCES.map(stance => (
              <button
                key={stance.id}
                onClick={() => handleStanceSelect(stance.id)}
                className={`p-6 text-left transition-all ${
                  localPrepCard.openingStance === stance.id
                    ? 'bg-[#0055A5] text-white shadow-ambient-strong'
                    : 'bg-white hover:bg-slate-50 text-[#002B45] shadow-ambient'
                }`}
                data-testid={`stance-${stance.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-sm font-headline font-semibold ${
                    localPrepCard.openingStance === stance.id ? 'text-white' : 'text-[#002B45]'
                  }`}>
                    {stance.label}
                  </span>
                  {localPrepCard.openingStance === stance.id && (
                    <Check className="w-5 h-5" strokeWidth={2} />
                  )}
                  {stance.recommended && localPrepCard.openingStance !== stance.id && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#78BE20] bg-[#78BE20]/10 px-2 py-0.5">
                      Recommended
                    </span>
                  )}
                </div>
                <p className={`text-sm ${
                  localPrepCard.openingStance === stance.id ? 'text-white/80' : 'text-[#475569]'
                }`}>
                  {stance.description}
                </p>
              </button>
            ))}
          </div>
        </ValidationPulse>
      </motion.section>

      {/* Needs to Understand Section */}
      <motion.section variants={fadeUp} className="mb-12" data-testid="prep-needs-to-understand">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-[#002B45]" strokeWidth={1.5} />
          <h2 className="text-xl font-headline font-semibold text-[#002B45]">
            Need to Understand
          </h2>
          {showUnderstandValidation && (
            <span className="text-xs font-semibold text-[#DA291C] animate-pulse">
              Select at least one
            </span>
          )}
        </div>
        
        <InstructionalFade hasInteracted={hasInteracted && localPrepCard.needsToUnderstand.length > 0}>
          What information must you gather before drawing conclusions?
        </InstructionalFade>
        
        <ValidationPulse show={showUnderstandValidation}>
          <div className="space-y-3">
            {NEEDS_TO_UNDERSTAND.map(item => (
              <button
                key={item.id}
              onClick={() => toggleNeedToUnderstand(item.id)}
              className={`w-full p-5 text-left flex items-center justify-between transition-all ${
                localPrepCard.needsToUnderstand.includes(item.id)
                  ? 'bg-[#F1F5F9]'
                  : 'bg-white hover:bg-slate-50'
              } shadow-ambient`}
              data-testid={`understand-${item.id}`}
            >
              <div>
                <span className="text-sm font-headline font-semibold text-[#002B45]">
                  {item.label}
                </span>
                <p className="text-sm text-[#475569] mt-1">
                  {item.description}
                </p>
              </div>
              <div className={`w-6 h-6 flex items-center justify-center ${
                localPrepCard.needsToUnderstand.includes(item.id)
                  ? 'bg-[#0055A5] text-white'
                  : 'bg-slate-100'
              }`}>
                {localPrepCard.needsToUnderstand.includes(item.id) && (
                  <Check className="w-4 h-4" strokeWidth={2} />
                )}
              </div>
            </button>
          ))}
          </div>
        </ValidationPulse>
      </motion.section>

      {/* Must Not Avoid Section */}
      <motion.section variants={fadeUp} className="mb-12" data-testid="prep-must-not-avoid">
        <div className="flex items-center gap-3 mb-6">
          <AlertCircle className="w-5 h-5 text-[#002B45]" strokeWidth={1.5} />
          <h2 className="text-xl font-headline font-semibold text-[#002B45]">
            Must Not Avoid
          </h2>
          {showAvoidValidation && (
            <span className="text-xs font-semibold text-[#DA291C] animate-pulse">
              Select at least one
            </span>
          )}
        </div>
        
        <InstructionalFade hasInteracted={hasInteracted && localPrepCard.mustNotAvoid.length > 0}>
          What outcomes are non-negotiable for this conversation?
        </InstructionalFade>
        
        <ValidationPulse show={showAvoidValidation}>
          <div className="space-y-3">
            {MUST_NOT_AVOID.map(item => (
              <button
                key={item.id}
                onClick={() => toggleMustNotAvoid(item.id)}
                className={`w-full p-5 text-left flex items-center justify-between transition-all ${
                  localPrepCard.mustNotAvoid.includes(item.id)
                    ? 'bg-[#F1F5F9]'
                    : 'bg-white hover:bg-slate-50'
                } shadow-ambient`}
                data-testid={`avoid-${item.id}`}
              >
                <div>
                  <span className="text-sm font-headline font-semibold text-[#002B45]">
                    {item.label}
                  </span>
                  <p className="text-sm text-[#475569] mt-1">
                    {item.description}
                  </p>
                </div>
                <div className={`w-6 h-6 flex items-center justify-center ${
                  localPrepCard.mustNotAvoid.includes(item.id)
                    ? 'bg-[#0055A5] text-white'
                    : 'bg-slate-100'
                }`}>
                  {localPrepCard.mustNotAvoid.includes(item.id) && (
                    <Check className="w-4 h-4" strokeWidth={2} />
                  )}
                </div>
              </button>
            ))}
          </div>
        </ValidationPulse>
      </motion.section>

      {/* Equity Lens Toggle */}
      <motion.section variants={fadeUp} className="mb-12" data-testid="prep-equity-lens">
        <button
          onClick={toggleEquityLens}
          className={`w-full p-6 text-left flex items-center justify-between transition-all ${
            localPrepCard.equityLens
              ? 'bg-[#0055A5] text-white'
              : 'bg-white hover:bg-slate-50 text-[#002B45]'
          } shadow-ambient`}
          data-testid="equity-toggle"
        >
          <div>
            <span className={`text-sm font-headline font-semibold ${
              localPrepCard.equityLens ? 'text-white' : 'text-[#002B45]'
            }`}>
              Keep Context and Equity in View
            </span>
            <p className={`text-sm mt-1 ${
              localPrepCard.equityLens ? 'text-white/80' : 'text-[#475569]'
            }`}>
              Consider whether bias, prior history, or unequal dynamics may be shaping how this moment is being experienced.
            </p>
          </div>
          <div className={`w-12 h-6 rounded-full relative transition-colors ${
            localPrepCard.equityLens ? 'bg-white/20' : 'bg-slate-200'
          }`}>
            <div className={`absolute top-1 w-4 h-4 rounded-full transition-transform ${
              localPrepCard.equityLens 
                ? 'bg-white translate-x-6' 
                : 'bg-slate-400 translate-x-1'
            }`} />
          </div>
        </button>
      </motion.section>

      {/* Actions */}
      <div className="flex items-center justify-between pt-8 border-t border-slate-100">
        <Button
          variant="ghost"
          onClick={() => setView('theory')}
          className="text-[#475569] hover:text-[#002B45]"
          data-testid="prep-back"
        >
          Back to Theory
        </Button>
        
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={handleSave}
            className="border-[#0055A5] text-[#0055A5] hover:bg-[#0055A5]/5"
            data-testid="prep-save"
          >
            Save Draft
          </Button>
          
          <Button
            onClick={handleStartSimulation}
            disabled={!isComplete}
            className={`px-8 ${
              isComplete 
                ? 'bg-[#0055A5] hover:bg-[#002B45] text-white'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="prep-start-simulation"
          >
            Enter Simulation
          </Button>
        </div>
      </div>

      {/* Completion Status */}
      {!isComplete && (
        <p className="text-sm text-[#DA291C] mt-4 text-right">
          Complete your plan before entering the conversation: choose an opening stance, identify what you still need to understand, and name at least one priority you will not avoid.
        </p>
      )}
    </motion.div>
  );
}

// Read-only Prep Card Summary (for HUD and Dashboard)
export function PrepCardSummary({ compact = false }) {
  const { state } = useSimulation();
  const { prepCard } = state;

  if (!prepCard.completed && !prepCard.openingStance) {
    return (
      <div className="p-4 bg-slate-50 text-center" data-testid="prep-card-empty">
        <FileText className="w-6 h-6 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-xs text-slate-400">Plan not configured</p>
      </div>
    );
  }

  const stanceLabel = OPENING_STANCES.find(s => s.id === prepCard.openingStance)?.label || 'Not Set';

  if (compact) {
    return (
      <div className="p-4 bg-[#F1F5F9]" data-testid="prep-card-summary-compact">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
          <span className="text-xs font-semibold uppercase tracking-wider text-[#0055A5]">
            Your Intent
          </span>
        </div>
        <p className="text-sm font-headline font-semibold text-[#002B45]">
          {stanceLabel}
        </p>
        {prepCard.equityLens && (
          <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wider text-[#78BE20] bg-[#78BE20]/10 px-2 py-0.5">
            Equity Lens Active
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 bg-white shadow-ambient" data-testid="prep-card-summary">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-[#0055A5]" strokeWidth={1.5} />
        <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
          Implementation Intent
        </span>
      </div>
      
      <div className="space-y-4">
        <div>
          <p className="text-xs text-[#475569] mb-1">Opening Stance</p>
          <p className="text-sm font-headline font-semibold text-[#002B45]">{stanceLabel}</p>
        </div>
        
        {prepCard.needsToUnderstand.length > 0 && (
          <div>
            <p className="text-xs text-[#475569] mb-1">Must Understand</p>
            <div className="flex flex-wrap gap-1">
              {prepCard.needsToUnderstand.map(id => (
                <span 
                  key={id}
                  className="text-[10px] font-medium uppercase tracking-wide text-[#002B45] bg-slate-100 px-2 py-0.5"
                >
                  {NEEDS_TO_UNDERSTAND.find(n => n.id === id)?.label}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {prepCard.mustNotAvoid.length > 0 && (
          <div>
            <p className="text-xs text-[#475569] mb-1">Must Not Avoid</p>
            <div className="flex flex-wrap gap-1">
              {prepCard.mustNotAvoid.map(id => (
                <span 
                  key={id}
                  className="text-[10px] font-medium uppercase tracking-wide text-[#002B45] bg-slate-100 px-2 py-0.5"
                >
                  {MUST_NOT_AVOID.find(n => n.id === id)?.label}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {prepCard.equityLens && (
          <div className="pt-2 border-t border-slate-100">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#78BE20]">
              Equity Lens Active
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PrepCardBuilder;
